import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import classes from "./Chats.module.css";
import { useParams } from "react-router-dom";
import { emitLiveComments, listenerLiveComments } from "../../../Socket/Socket";
import axios from "axios";
import { useSelector } from "react-redux";

const konn = "https://milegrid.konnect.im";

// Messages from the same author sent close together are visually stacked, so the
// avatar and name only appear once per burst.
const GROUPING_WINDOW_MS = 5 * 60 * 1000;

const AVATAR_COLORS = [
  "#526bb1",
  "#8b5cf6",
  "#0ea5e9",
  "#f59e0b",
  "#10b981",
  "#ef4444",
  "#ec4899",
  "#14b8a6",
];

const getInitials = (name) => {
  if (!name) return "?";
  const words = name.trim().split(/\s+/);
  if (words.length > 1) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return words[0].slice(0, 2).toUpperCase();
};

const getAvatarColor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) % 997;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const Avatar = ({ user }) => {
  const name = user?.name || "Guest";
  return user?.image ? (
    <img className={classes.avatar} src={user.image} alt={name} />
  ) : (
    <span
      className={`${classes.avatar} ${classes.avatarFallback}`}
      style={{ backgroundColor: getAvatarColor(name) }}
      aria-hidden="true"
    >
      {getInitials(name)}
    </span>
  );
};

export const Chats = ({ isOpen, onIncomingMessage }) => {
  const userInfo = useSelector((state) => state.slide.userInformation);

  const [messageArr, setMessageArr] = useState([]);
  const [draft, setDraft] = useState("");
  const [hasNewBelow, setHasNewBelow] = useState(false);

  const listRef = useRef(null);
  const inputRef = useRef(null);
  // The socket listener is registered once, so route the "new message" callback
  // through a ref to always hit the current one.
  const onIncomingMessageRef = useRef(onIncomingMessage);
  onIncomingMessageRef.current = onIncomingMessage;
  const isPinnedToBottomRef = useRef(true);

  const { id } = useParams();

  useEffect(() => {
    axios.post(`${konn}/api/update`, {
      spreadsheetID: id,
      Comments: messageArr,
    });
  }, [id, messageArr]);

  const scrollToBottom = useCallback((behavior = "smooth") => {
    const list = listRef.current;
    if (!list) return;
    list.scrollTo({ top: list.scrollHeight, behavior });
    isPinnedToBottomRef.current = true;
    setHasNewBelow(false);
  }, []);

  // Follow the conversation only while the reader is already at the bottom;
  // otherwise offer a "jump to latest" pill instead of yanking them down.
  useEffect(() => {
    if (messageArr.length === 0) return;
    if (isPinnedToBottomRef.current) {
      scrollToBottom();
    } else {
      setHasNewBelow(true);
    }
  }, [messageArr, scrollToBottom]);

  useEffect(() => {
    if (!isOpen) return;
    scrollToBottom("auto");
    inputRef.current?.focus();
  }, [isOpen, scrollToBottom]);

  const handleScroll = () => {
    const list = listRef.current;
    if (!list) return;
    const distanceFromBottom = list.scrollHeight - list.scrollTop - list.clientHeight;
    isPinnedToBottomRef.current = distanceFromBottom < 40;
    if (isPinnedToBottomRef.current) setHasNewBelow(false);
  };

  const appendMessage = useCallback((data) => {
    setMessageArr((prevState) => [...prevState, data]);
  }, []);

  useEffect(() => {
    listenerLiveComments((data) => {
      appendMessage({ ...data, self: false, receivedAt: Date.now() });
      onIncomingMessageRef.current?.();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendMessage = (event) => {
    event.preventDefault();
    const msg = draft.trim();
    if (!msg) return;

    // color/margin stay on the payload for older clients that still style from
    // them; this client derives its own layout from `self`.
    const chatData = {
      id,
      msg,
      color: "#e9e9e9",
      margin: "5px 0px 5px auto",
      userInfo,
      time: Date.now(),
    };

    appendMessage({ ...chatData, self: true });
    emitLiveComments(chatData);
    setDraft("");
    isPinnedToBottomRef.current = true;
    inputRef.current?.focus();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      sendMessage(event);
    }
  };

  const rows = useMemo(
    () =>
      messageArr.map((message, index) => {
        const previous = messageArr[index - 1];
        const isSelf =
          message.self ?? message.userInfo?.name === userInfo?.name;
        const timestamp = message.time || message.receivedAt;
        const previousTimestamp = previous?.time || previous?.receivedAt;
        const startsGroup =
          !previous ||
          (previous.self ?? previous.userInfo?.name === userInfo?.name) !== isSelf ||
          previous.userInfo?.name !== message.userInfo?.name ||
          !timestamp ||
          !previousTimestamp ||
          timestamp - previousTimestamp > GROUPING_WINDOW_MS;

        return { message, isSelf, startsGroup, timestamp, key: `${index}-${timestamp || ""}` };
      }),
    [messageArr, userInfo]
  );

  const canSend = draft.trim().length > 0;

  return (
    <>
      <div
        className={classes.list}
        id="myChat"
        ref={listRef}
        onScroll={handleScroll}
        role="log"
        aria-live="polite"
      >
        {rows.length === 0 ? (
          <div className={classes.emptyState}>
            <span className={classes.emptyIcon} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="26" height="26" fill="none">
                <path
                  d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <p className={classes.emptyTitle}>No comments yet</p>
            <p className={classes.emptyHint}>
              Start the conversation — everyone viewing this deck will see it live.
            </p>
          </div>
        ) : (
          rows.map(({ message, isSelf, startsGroup, timestamp, key }) => (
            <div
              key={key}
              className={`${classes.row} ${isSelf ? classes.rowSelf : classes.rowOther} ${
                startsGroup ? classes.rowGroupStart : ""
              }`}
            >
              {!isSelf && (
                <div className={classes.avatarSlot}>
                  {startsGroup && <Avatar user={message.userInfo} />}
                </div>
              )}
              <div className={classes.bubbleColumn}>
                {!isSelf && startsGroup && (
                  <span className={classes.senderName}>
                    {message.userInfo?.name || "Guest"}
                  </span>
                )}
                <div className={`${classes.bubble} ${isSelf ? classes.bubbleSelf : classes.bubbleOther}`}>
                  <span className={classes.bubbleText}>{message.msg}</span>
                  {timestamp && (
                    <time className={classes.timestamp} dateTime={new Date(timestamp).toISOString()}>
                      {formatTime(timestamp)}
                    </time>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {hasNewBelow && (
        <button type="button" className={classes.jumpButton} onClick={() => scrollToBottom()}>
          New messages
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true">
            <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      <form onSubmit={sendMessage} className={classes.composer}>
        <input
          className={classes.input}
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Write a comment…"
          aria-label="Write a comment"
          maxLength={500}
        />
        <button
          type="submit"
          className={classes.sendButton}
          disabled={!canSend}
          aria-label="Send comment"
          title="Send comment"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
            <path
              d="M4.4 11.5 19.6 4.4a.6.6 0 0 1 .8.8l-7.1 15.2a.6.6 0 0 1-1.1-.1l-1.9-6-6-1.9a.6.6 0 0 1-.1-1.1Z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </form>
    </>
  );
};
